import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";
import { BUSINESS } from "../../lib/businessInfo";

export default function ForgotPasswordPage() {
  const { resetPassword, isAuthenticated, loading, isDevMode, emailVerified } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [devSkipped, setDevSkipped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) {
    if (!isDevMode && !emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }
    return <Navigate to="/instructor" replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await resetPassword(email.trim());
      setDevSkipped(Boolean(result?.skipped));
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Unable to send reset email.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center px-4">
      <Seo
        title="Forgot Password"
        description="Reset your Yoga Retreats Booking instructor password."
        canonicalPath="/forgot-password"
        robots="noindex,nofollow"
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <BrandMark />
          <span className="font-bold text-gray-900">
            Yoga Retreats <span className="text-emerald-600">Booking</span>
          </span>
        </Link>
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Forgot password</h1>
          <p className="text-sm text-gray-500 mb-6">
            Enter your instructor email and we will send a reset link.
          </p>

          {submitted ? (
            <div className="space-y-4">
              {devSkipped ? (
                <div className="rounded-lg bg-amber-50 text-amber-900 text-sm px-3 py-3 leading-relaxed border border-amber-100">
                  Password reset emails are not sent in local dev auth mode. Use your existing dev
                  password, or contact us at {BUSINESS.email} if you need help on the live site.
                </div>
              ) : (
                <div className="rounded-lg bg-emerald-50 text-emerald-800 text-sm px-3 py-3 leading-relaxed">
                  If an account exists for <span className="font-medium">{email.trim()}</span>, you
                  will receive a password reset email shortly. Check your inbox and spam folder,
                  then sign in with your new password.
                </div>
              )}
              <Link
                to="/login"
                className="block w-full text-center rounded-lg bg-emerald-600 text-white font-medium py-2.5 hover:bg-emerald-700"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Email</span>
                  <input
                    type="email"
                    required
                    name="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-emerald-600 text-white font-medium py-2.5 hover:bg-emerald-700 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <p className="mt-6 text-sm text-center text-gray-600">
                Remembered your password?{" "}
                <Link to="/login" className="text-emerald-700 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
